import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { isEmpty } from 'lodash';
import cn from 'classnames';
import { Tab } from '@headlessui/react';
import { useRouter } from 'next/router';

import { Icon, IconButton, Spinner, Image } from 'components';
import { getLayout } from 'components/layouts/Sidebar';
import { PendingInviteCard } from 'components/PendingInviteCard/PendingInviteCard.component';
import InviteCommitteeCard from 'components/InviteCommitteeCard';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import Pagination from 'components/Pagination';
import { useNotification } from 'hooks/useNotification';
import { useSocietyProfile } from 'pages/society-profile/UseSocietyProfile.hook';
import { useCommunityBoard } from 'pages/community/useCommunityBoard';
import { useCommunityInfo } from 'pages/community/useCommunityInfo';
import SubscribersTable from 'pages/community/CommunityTable';
import { LOCALE_PAGES, fetchTranslationFiles } from 'lib/services/page';
import { withAuth } from 'lib/hocs/withAuth';
import { PAGE_ROUTES } from 'lib/page-routes';
import { parseErrorResponse } from 'lib/utils/parser';
import { CommunityAttributes, CommunityTypes } from 'lib/models/community';
import { DashboardHeader } from './DashboardHeader/DashboardHeader.component';
import { SocietyDashboardInfo } from './SocietyDashboardInfo/SocietyDashboardInfo.component';
import { GetHuzzleVerified } from './GetHuzzleVerified/GetHuzzleVerified.component';
import { MoreFromHuzzle } from './MoreFromHuzzle/MoreFromHuzzle.component';
import { QRCodeModal } from './QRCodeModal/QRCodeModal.component';
import { EditShortLinkModal } from './EditShortLinkModal/EditShortLinkModal.component';
import { AddSubscribers } from './AddSubscribers/AddSubscribers.component';

import styles from './Dashboard.module.scss';

const Dashboard = () => {
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [showQRModal, setShowQRModal] = useState<boolean>(false);
  const [updateSlugLoading, setUpdateSlugLoading] = useState<boolean>(false);
  const [showEditLinkModal, setShowEditLinkModal] = useState<boolean>(false);
  const [shortLinkError, setShortLinkError] = useState<string>('');
  const [showBanner, setShowBanner] = useState(true);
  const notificationInstance = useNotification();
  const router = useRouter();
  const {
    societyDashboard,
    isSocietyDashboardLoading: dashboardDataFetching,
    addNewSubscriber,
    societyProfile,
    onSocietyProfileUpdate,
  } = useSocietyProfile();

  const onScroll = (event: React.UIEvent<HTMLElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 100);
  };

  const toggleQRModal = () => {
    setShowQRModal(!showQRModal);
  };
  const toggleEditShortLinkModal = () => {
    setShortLinkError('');
    setShowEditLinkModal(!showEditLinkModal);
  };
  const {
    dashboardData,
    mutateCommunityDashboard,
    onApproveSubscriptionRequests,
    onDeclineSubscriptionRequests,
    onDeleteMember,
    onCSVExport,
    sendMessage,
  } = useCommunityBoard();

  const { subscription_requests_count = 0, attendees_count = 0 } =
    dashboardData || {};
  const setDefaultCommunityType = () => {
    if (subscribers_require_approval && subscription_requests_count > 0) {
      onChangeCommunityType(CommunityTypes.SUBSCRIPTION_REQUESTS);
      setSelectedState(0);
    } else {
      onChangeCommunityType(CommunityTypes.SUBSCRIBED);
      setSelectedState(0);
    }
  };
  const {
    applications,
    isLoading,
    isLoadingMore,
    mutate,
    applicationsMetaInfo,
    selectedCommunityType,
    onChangeCommunityType,
    pagesIndex,
    setpagesIndex,
    per_page_count,
  } = useCommunityInfo();
  const {
    subscribers_require_approval = false,
    invite_later_banner_hidden = true,
  } = societyProfile || {};
  useEffect(() => {
    if (
      subscription_requests_count &&
      selectedCommunityType === CommunityTypes.SUBSCRIPTION_REQUESTS
    ) {
      mutate();
    }
    if (selectedCommunityType === CommunityTypes.ATTENDEES) {
      setShowBanner(true);
    }
  }, [subscription_requests_count, selectedCommunityType]);
  useEffect(() => {
    if (
      (!subscribers_require_approval || subscription_requests_count === 0) &&
      selectedCommunityType === CommunityTypes.SUBSCRIPTION_REQUESTS
    ) {
      onChangeCommunityType(CommunityTypes.SUBSCRIBED);
      setSelectedState(0);
      mutate();
    } else if (selectedCommunityType) {
      onChangeCommunityType(selectedCommunityType);
      const selectedIndex = SubriberStateOption.findIndex(
        (option) => option.value === selectedCommunityType
      );
      if (selectedIndex !== -1) {
        setSelectedState(selectedIndex);
      }
      mutate();
    } else {
      setDefaultCommunityType();
    }
  }, [
    selectedCommunityType,
    subscribers_require_approval,
    subscription_requests_count,
  ]);

  const copyProfileLink = () => {
    navigator.clipboard.writeText(
      societyProfile.short_link || societyProfile.public_url
    );
    notificationInstance.success({
      title: 'Link copied',
      message: 'A link to society page has been copied to your clipboard!',
    });
  };
  const getEmptyCommunityContent = (option: string) => {
    switch (option) {
      case CommunityTypes.SUBSCRIPTION_REQUESTS:
        return 'No membership request yet';
      case CommunityTypes.SUBSCRIBED:
        return 'No members yet';
      case CommunityTypes.UNSUBSCRIBED:
        return 'No past members yet';
      case CommunityTypes.INVITED:
        return 'No one invited to your community yet';
      case CommunityTypes.ATTENDEES:
        return 'No attendees yet';
      default:
        return '';
    }
  };
  const emptyCommunityContent = () => {
    return (
      <section className={styles.CommunityEmptyContainer}>
        <Image
          src={'/images/desert.png'}
          className={styles.CommunityEmptyContainerImg}
        />
        <p className={styles.CommunityEmptyContainerHeading}>
          {getEmptyCommunityContent(selectedCommunityType)}
        </p>
        <div className={styles.CommunityEmptyContainerDescrip}>
          <p>
            Share your profile across your socials to convert followers to
            members. Or host an event to gain more traction.
          </p>
          <p>
            Members receive automatic notifications about upcoming events,
            helping you keep them engaged.
          </p>
        </div>
        <div className={styles.buttonContainer}>
          <AddSubscribers className={styles.addSubst} />
          <ButtonV3
            size="xlarge"
            color="secondary"
            onClick={() =>
              router.push(`${PAGE_ROUTES.EVENTS_CREATE}?step=1&type=Event`)
            }
          >
            Create event
          </ButtonV3>
        </div>
      </section>
    );
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}${PAGE_ROUTES.MANAGER_SIGN_UP}`
    );
    notificationInstance.success({
      title: 'Link copied',
      message:
        'A link to invite committee members has been copied to your clipboard!',
    });
  };
  const [selectedState, setSelectedState] = useState(0);

  const SubriberStateOption = [
    ...(subscribers_require_approval && subscription_requests_count > 0
      ? [
          {
            id: '1',
            value: CommunityTypes.SUBSCRIPTION_REQUESTS,
            label: 'Membership requests',
          },
        ]
      : []),
    {
      id: '2',
      value: CommunityTypes.SUBSCRIBED,
      label: 'Members',
    },
    {
      id: '3',
      value: CommunityTypes.INVITED,
      label: 'Invited',
    },
    {
      id: '4',
      value: CommunityTypes.ATTENDEES,
      label: 'Attendees',
    },
    {
      id: '5',
      value: CommunityTypes.UNSUBSCRIBED,
      label: 'Past members',
    },
  ];
  const [selectedRows, setSelectedRows] = useState<Array<string>>([]);

  const handleSelectRows = (selected: boolean, id: string) => {
    setSelectedRows((prevRows) => {
      const updatedSelectedRows = selected
        ? [...prevRows, id]
        : prevRows.filter((rowId) => rowId !== id);
      return updatedSelectedRows;
    });
  };

  const selectAll = () => {
    if (selectedRows.length === applications.length) {
      setSelectedRows([]);
    } else {
      const tempList = applications.map(
        (application: CommunityAttributes) => application.id
      );
      setSelectedRows(tempList);
    }
  };
  const onInviteAttendee = async (attendee: {
    first_name: string;
    last_name: string;
    email: string;
  }) => {
    try {
      await addNewSubscriber(attendee);
    } catch (error) {
      console.error(error);
    }
  };

  const changeShortSlug = async (short_slug: string) => {
    setShortLinkError('');
    try {
      setUpdateSlugLoading(true);
      await onSocietyProfileUpdate({ short_slug }, true);
      toggleEditShortLinkModal();
      setUpdateSlugLoading(false);
      notificationInstance.success({
        title: 'Success',
        message: 'Short link updated.',
      });
    } catch (error) {
      setUpdateSlugLoading(false);
      setShortLinkError(parseErrorResponse(error as Error));
    }
  };
  return (
    <>
      <Head>
        <title>Dashboard | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      {dashboardDataFetching || isEmpty(societyProfile) ? (
        <Spinner wrapper />
      ) : (
        <>
          <div className={styles.SocietyDashboard} onScroll={onScroll}>
            {updateSlugLoading && <Spinner showBackDrop size="small" />}
            <DashboardHeader
              isScrolled={isScrolled}
              shortUrl={societyProfile.short_link || societyProfile.public_url}
              openQRModal={toggleQRModal}
              openShortLinkModal={toggleEditShortLinkModal}
              copyLink={copyProfileLink}
            />
            <div className={styles.SocietyDashboardContent}>
              <InviteCommitteeCard />
              <PendingInviteCard />
              {societyDashboard ? (
                <SocietyDashboardInfo dashboardInfo={societyDashboard} />
              ) : (
                <Spinner wrapper wrapperClassname={styles.loader}></Spinner>
              )}
              {societyDashboard && !societyDashboard.huzzle_verified && (
                <GetHuzzleVerified
                  openShortLinkModal={toggleEditShortLinkModal}
                  copyInviteLink={copyInviteLink}
                  dashboardData={societyDashboard}
                  copyProfileLink={copyProfileLink}
                />
              )}

              <div className={styles.subscribersListSection}>
                <p className={styles.memberHeading}>Members</p>
                <Row
                  align="center"
                  justify="space-between"
                  className={styles.subscribersListSectionHeader}
                >
                  <div className={styles.SubcribersTab}>
                    <Tab.Group
                      selectedIndex={selectedState}
                      onChange={(index) => {
                        setSelectedState(index);
                        onChangeCommunityType(SubriberStateOption[index].value);
                        setpagesIndex(1);
                      }}
                    >
                      <Tab.List className={styles.tabList}>
                        {SubriberStateOption.map((tab, index) => {
                          return (
                            <React.Fragment key={tab.id}>
                              {index !== 0 &&
                              index !== selectedState &&
                              index !== selectedState + 1 ? (
                                <span className={styles.seperator}></span>
                              ) : null}
                              <Tab
                                key={tab.id}
                                className={({ selected }) =>
                                  cn(
                                    styles.tab,
                                    { [styles.tabSelected]: selected },
                                    {
                                      [styles.tabDisabled]: false,
                                    }
                                  )
                                }
                              >
                                {tab.label}
                              </Tab>
                            </React.Fragment>
                          );
                        })}
                      </Tab.List>
                    </Tab.Group>
                  </div>
                  <Row
                    align="center"
                    className={styles.subscribersListSectionHeaderBtnRow}
                  >
                    <AddSubscribers className={styles.addSubs} />

                    <ButtonV3
                      className={styles.subscribersListSectionHeaderBtn}
                      color="secondary"
                      size="small"
                      onClick={() => {
                        if (selectedCommunityType) {
                          onCSVExport(selectedCommunityType as CommunityTypes);
                        }
                      }}
                    >
                      Export CSV
                    </ButtonV3>
                  </Row>
                </Row>
                {selectedCommunityType === CommunityTypes.ATTENDEES &&
                  showBanner && (
                    <div className={styles.bannerText}>
                      <p>
                        <Icon iconName="icon_info" />
                        These are attendees who haven’t joined your community
                        yet.
                      </p>
                      <IconButton
                        onClick={() => setShowBanner(false)}
                        iconName="icon_close"
                        className={styles.icon}
                      />
                    </div>
                  )}

                <div className={styles.content}>
                  <section
                    className={styles.CandidatesInfo}
                    data-testid="CandidatesInfo"
                  >
                    <SubscribersTable
                      key={selectedCommunityType}
                      isLoading={isLoading}
                      rows={applications}
                      communityType={selectedCommunityType}
                      setIsAllRowsSelected={selectAll}
                      isAllRowsSelected={
                        applications.length > 0 &&
                        selectedRows.length === applications.length
                      }
                      selectedRowsIds={selectedRows}
                      setSelectedRows={(...params) =>
                        handleSelectRows(...params)
                      }
                      clearSelectRows={() => setSelectedRows([])}
                      showCheckbox={
                        selectedCommunityType ===
                        CommunityTypes.SUBSCRIPTION_REQUESTS
                      }
                      refetch={mutate}
                      reftechDashboard={mutateCommunityDashboard}
                      onApproval={onApproveSubscriptionRequests}
                      onRejection={onDeclineSubscriptionRequests}
                      onInviteAttendee={onInviteAttendee}
                      onSendMessage={sendMessage}
                      onDelete={onDeleteMember}
                    />
                    {!isLoading && isLoadingMore ? (
                      <Spinner
                        size="small"
                        className={styles.spinner}
                        wrapperClassname={styles.spinnerWrapper}
                        wrapper
                      />
                    ) : null}

                    <Pagination
                      currentPage={pagesIndex}
                      onPageChange={(page: number) => setpagesIndex(page)}
                      pageSize={per_page_count}
                      totalCount={applicationsMetaInfo.total || 0}
                      showShowingData={true}
                      className={styles.pagination}
                    />
                    {applicationsMetaInfo.total === 0 &&
                      !isLoading &&
                      emptyCommunityContent()}
                  </section>
                </div>
              </div>

              <MoreFromHuzzle
                copyLink={copyProfileLink}
                openQRModal={toggleQRModal}
                copyInviteLink={copyInviteLink}
              />
            </div>
          </div>
          <QRCodeModal
            open={showQRModal}
            societyName={societyProfile.name}
            qrCodeUrl={societyProfile.short_link_qr_code_url}
            slug={societyProfile.slug}
            onClose={toggleQRModal}
          />
          <EditShortLinkModal
            open={showEditLinkModal}
            onClose={toggleEditShortLinkModal}
            slug={societyProfile.short_slug}
            changeShortSlug={changeShortSlug}
            error={shortLinkError}
          />
        </>
      )}
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.COMMON],
      })),
    },
  };
}

const DashboardWrapper = withAuth(Dashboard);

// @ts-ignore
DashboardWrapper.getLayout = getLayout;

export default DashboardWrapper;
